import { init, captureException, setTag, setUser } from '@sentry/browser';

import type { ErrorLoggingConfiguration } from './errorLogging.types';
import { Severity } from './errorLogging.types';

/**
 * @see @sentry/browser/User for more details on the User if you would like to add more properties to this type.
 */
type ErrorLoggingUser = {
    id: string;
} & Record<string, unknown>;

export const initializeErrorLogging = (configuration: ErrorLoggingConfiguration): void => {
    if (configuration.dataSourceName) {
        init({
            dsn: configuration.dataSourceName,
            environment: configuration.environment ? configuration.environment : 'unknown',
            release: configuration.release,
            beforeSend: configuration.beforeSend,
            ignoreErrors: [
                // Ignore errors related to CEFSharp: https://github.com/DataDog/browser-sdk/issues/2715
                /Object Not Found Matching Id:\d+/,
            ],
        });
    }
};

export const setLoggingTag = (tagKey: string, value: string) => {
    setTag(tagKey, value);
};

/**
 * Log an exception with the configured destination (e.g. Sentry)
 * @param error The error or error message to log
 * @param severity Defaults to 'Error' but should be set to 'Fatal' if application flow cannot continue and user cannot easily recover from the error.
 * @param additionalInfo Optional information that will be recorded with the error. Take care not to record sensitive data.
 */
export const logException = (
    error: Error | string,
    severity: Severity = Severity.Error,
    additionalInfo?: Record<string, any>,
): string => {
    const context = {
        level: severity,
        extra: additionalInfo ?? undefined,
    };

    if (error instanceof Error) {
        return captureException(error, context);
    }
    const e = new Error(error);
    return captureException(e, context);
};

/**
 * Helper function to be used in try/catch blocks when the error type is 'unknown'
 * @param error The 'unknown' error object, as caught by default in a catch block
 * @param severity Defaults to 'Error' but should be set to 'Fatal' if application flow cannot continue and user cannot easily recover from the error.
 * @param fallbackMessage If the error object is not an instance of 'Error', then the fallback message is used.
 * @param additionalInfo Optional information that will be recorded with the error. Take care not to record sensitive data.
 * @returns The ID of the logged error that was logged in the error logging service (e.g. Sentry)
 */
export const logUnknownException = (
    error: unknown,
    severity: Severity = Severity.Error,
    fallbackMessage = 'An error has occurred',
    additionalInfo?: Record<string, any>,
): string => {
    if (error instanceof Error) {
        return logException(error, severity);
    } else if (error instanceof Object) {
        return logException(fallbackMessage, severity, {
            errorObj: JSON.stringify(error),
            additionalInfo,
        });
    } else {
        return logException(fallbackMessage, severity, additionalInfo);
    }
};

/**
 * Expose the sentry setUser function so that dependant apps don't need to import @sentry/browser
 */
export const setErrorLoggingUser = (user: ErrorLoggingUser | null): void => {
    setUser(user);
};
